import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import TimezoneIcon from '@/assets/icons/icon-globe-time.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $account } from '@/models/account.ts'
import { openDialog } from '@/models/dialogs.ts'
import { getUTCOffset } from '@/modules/chatting/ui/chat-window/fan-info/consts.ts'
import { useDesktopApp } from '@/modules/desktop/DesktopAppProvider.tsx'
import { AppRoutes } from '@/utils/constants'

import IconChevronDown from '../../../assets/icons/icon-chevron-down.svg'
import IconExit from '../../../assets/icons/icon-exit.svg'
import IconExternal from '../../../assets/icons/icon-external.svg'
import IconSettings from '../../../assets/icons/icon-settings.svg'
import IconTroubleshooting from '../../../assets/icons/icon-troubleshooting.svg'
import { useAccount } from '../hooks/useAccounts'
import { AccountAccordion } from './AccountAccordion/AccountAccordion'

export const AccountMenu = () => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const account = useUnit($account)
  const { logout } = useAccount()
  const { isApp, sendCustomAppAction } = useDesktopApp()
  const timezone = account?.timezone
    ? `UTC${getUTCOffset(account.timezone)}`
    : null

  const handleLinkClick = useCallback(() => {
    setOpen(false)
  }, [])
  const handleTroubleshootingClick = useCallback(() => {
    sendCustomAppAction('open-troubleshooting')
    handleLinkClick()
  }, [handleLinkClick, sendCustomAppAction])
  const handleAppDownloadClick = useCallback(() => {
    window.open(AppRoutes.Help.DownloadApp.asUrl(), '_blank')
    handleLinkClick()
  }, [handleLinkClick])
  const handleLogout = useCallback(async () => {
    const hasAccountsInStorage = localStorage.getItem(LocalStorageKeys.ACCOUNTS)

    openDialog({
      type: DIALOG_TYPE.CONFIRMATION,
      title: 'Log Out',
      body: `Are you sure you want to log out?${hasAccountsInStorage ? ' You will be logged out from all accounts.' : ''}`,
      confirmText: 'Log Out',
      confirmButtonVariant: 'destructive',
      confirmButtonTestId: 'log-out-confirm-button',
      onConfirm: async () => {
        logout()
      },
    })
  }, [logout])
  const handleTimezoneSettingsClick = useCallback(() => {
    navigate(AppRoutes.Profile.GeneralSettings.asUrl())
  }, [navigate])

  return (
    <div className="relative flex gap-6">
      <Tooltip
        content="All statistics reflect your personal timezone setting. Daily totals will differ from OnlyFans, as OnlyFans strictly operates in UTC."
        contentClassName="max-w-[280px] text-left"
      >
        <Button
          variant="ghost"
          className="flex items-center justify-start gap-2 border bg-card px-3 font-normal text-title"
          disabled={!timezone}
          onClick={handleTimezoneSettingsClick}
        >
          <TimezoneIcon className="h-6 w-6" />

          {timezone}
        </Button>
      </Tooltip>

      <DropdownMenu
        open={open}
        onOpenChange={setOpen}
      >
        <DropdownMenuTrigger
          data-testid="account-menu-trigger"
          className="bg-secondary-50 dark:bg-secondary-600 h-[40px] border rounded-md px-4"
        >
          <div className="flex items-center gap-2 ">
            <img
              src={account?.profilePicture}
              data-testid="account-menu-trigger-avatar"
              alt="avatar"
              className="nsfw-ignore bg-border h-6 w-6 rounded-full"
            />
            <IconChevronDown className="text-title ml-auto h-4 w-4 flex-shrink-0" />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          data-testid="account-menu-content"
          className="w-[312px]"
          align="end"
          forceMount
        >
          <DropdownMenuLabel className="mb-[12px] p-0 font-normal">
            <AccountAccordion />
          </DropdownMenuLabel>
          <DropdownMenuGroup className="mt-[20px] flex flex-col gap-[16px]">
            <Link
              to={AppRoutes.Profile.Settings.asUrl()}
              onClick={handleLinkClick}
              data-testid="account-settings-link-menu"
            >
              <DropdownMenuItem className="text-title hover:bg-primary-500/20 hover:text-primary-500 group -m-2 h-[40px] justify-between rounded-md p-2">
                Account Settings
                <IconSettings className="text-secondary-300 group-hover:text-primary-500" />
              </DropdownMenuItem>
            </Link>
            {isApp && (
              <a
                href="#"
                onClick={handleTroubleshootingClick}
                data-testid="troubleshooting-link-menu"
              >
                <DropdownMenuItem className="text-title hover:bg-primary-500/20 hover:text-primary-500 group -m-2 h-[40px] justify-between rounded-md p-2">
                  Troubleshooting
                  <IconTroubleshooting className="text-secondary-300 group-hover:text-primary-500" />
                </DropdownMenuItem>
              </a>
            )}
            {!isApp && (
              <a
                href="#"
                onClick={handleAppDownloadClick}
              >
                <DropdownMenuItem
                  data-testid="download-app-link-menu"
                  className="text-title hover:bg-primary-500/20 hover:text-primary-500 group -m-2 h-[40px] justify-between rounded-md p-2"
                >
                  Download App
                  <IconExternal className="text-secondary-300 group-hover:text-primary-500" />
                </DropdownMenuItem>
              </a>
            )}
            <DropdownMenuItem
              data-testid="log-out-menu"
              onClick={handleLogout}
              className="text-danger-default hover:bg-danger-default/20 group -m-2 justify-between rounded-md p-2 font-medium"
            >
              Log Out
              <IconExit className="text-secondary-300 group-hover:text-danger-default" />
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
